import React, { useEffect, useState } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Table, 
  Tag, 
  Space, 
  Typography, 
  Descriptions, 
  Tabs, 
  Button,
  Progress,
  Alert,
  Empty,
  Spin
} from 'antd';
import { 
  ArrowLeftOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
  HomeOutlined,
  FileTextOutlined,
  HistoryOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import { ApiServices } from '../../services/apiServices';
import { ApiConfig, driversListUrl, warehousesListUrl } from '../../services/ApiConfigs';
import { routes } from '../../utils/routes.config';
import moment from 'moment';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const StoreDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  
  // Store data
  const [store, setStore] = useState<any>(null);
  const [storePlan, setStorePlan] = useState<any>(null);
  const [planHistory, setPlanHistory] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [orders, setOrders] = useState<any[]>([]);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [warehouses, setWarehouses] = useState<any[]>([]);

  useEffect(() => {
    if (id) {
      fetchStoreDetails();
    }
  }, [id]);

  const fetchStoreDetails = async () => {
    try {
      setLoading(true);
      
      // Fetch store details
      const storeRes = await ApiServices('get', `${ApiConfig.STORES}/${id}`);
      if (storeRes.success) {
        setStore(storeRes.data);
      } else {
        setStore(storeRes.data || storeRes);
      }
      
      // Fetch store plan
      try {
        const planRes = await ApiServices('get', `${ApiConfig.PLANS_STORE}/${id}`);
        if (planRes.success && planRes.data) {
          setStorePlan(planRes.data);
        }
      } catch (error) {
        console.log('No plan found for store');
      }
      
      // Fetch plan history
      try {
        const historyRes = await ApiServices('get', `${ApiConfig.PLANS_STORE}/${id}/history`);
        if (historyRes.success) {
          setPlanHistory(historyRes.data || []);
        }
      } catch (error) {
        console.log('Error fetching plan history');
      }
      
      // Fetch invoices
      try {
        const invoicesRes = await ApiServices('get', `${ApiConfig.PLANS_STORE}/${id}/invoices`);
        if (invoicesRes.success) {
          setInvoices(invoicesRes.data || []);
        }
      } catch (error) {
        console.log('Error fetching invoices');
      }
      
      // Fetch orders
      try {
        const ordersRes = await ApiServices('get', `${ApiConfig.getallOrderByStoreId}/${id}`);
        if (ordersRes.success) {
          setOrders(ordersRes.data || []);
        } else {
          setOrders(ordersRes.data || ordersRes || []);
        }
      } catch (error) {
        console.log('Error fetching orders');
      }
      
      // Fetch drivers for this store
      try {
        const driversRes = await ApiServices('get', driversListUrl(id));
        if (driversRes.status === 200 || driversRes.success) {
          setDrivers(Array.isArray(driversRes.data) ? driversRes.data : []);
        }
      } catch (error) {
        console.log('Error fetching drivers');
      }
      
      // Fetch warehouses
      try {
        const warehousesRes = await ApiServices('get', warehousesListUrl(id));
        if (warehousesRes.status === 200 || warehousesRes.success) {
          setWarehouses(Array.isArray(warehousesRes.data) ? warehousesRes.data : []);
        }
      } catch (error) {
        console.log('Error fetching warehouses');
      }
      
    } catch (error) {
      console.error('Error fetching store details:', error);
    } finally {
      setLoading(false);
    }
  };

  const getUsagePercentage = (current: number, max: number) => {
    if (!max || max === 0) return 0;
    return Math.min((current / max) * 100, 100);
  };

  const getStatusColor = (status: string) => {
    const colors: any = {
      pending: 'orange',
      paid: 'green',
      failed: 'red',
      refunded: 'blue',
      cancelled: 'default',
      draft: 'default',
      sent: 'blue',
    };
    return colors[status] || 'default';
  };

  // Plan History Columns
  const planHistoryColumns = [
    {
      title: 'Date',
      key: 'date',
      render: (_: any, record: any) => moment(record.createdAt).format('MMM DD, YYYY'),
    },
    {
      title: 'Plan',
      key: 'plan',
      render: (_: any, record: any) => record.plan?.name || 'N/A',
    },
    {
      title: 'Type',
      dataIndex: 'chargeType',
      key: 'chargeType',
      render: (type: string) => (
        <Tag color={type === 'one_time' ? 'purple' : type === 'renewal' ? 'cyan' : 'blue'}>
          {type?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Amount',
      key: 'amount',
      render: (_: any, record: any) => (
        <Text strong>
          {record.currencySymbol} {record.amount}
        </Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{status?.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Period',
      key: 'period',
      render: (_: any, record: any) => {
        if (record.chargeType === 'one_time') return 'Lifetime';
        return record.billingPeriod || 'N/A';
      },
    },
    {
      title: 'Invoice',
      key: 'invoice',
      render: (_: any, record: any) => 
        record.invoice ? (
          <Tag color="blue">{record.invoice.invoiceNumber}</Tag>
        ) : 'N/A',
    },
  ];

  // Invoices Columns
  const invoicesColumns = [
    {
      title: 'Invoice #',
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber',
    },
    {
      title: 'Date',
      dataIndex: 'issueDate',
      key: 'issueDate',
      render: (date: string) => moment(date).format('MMM DD, YYYY'),
    },
    {
      title: 'Plan',
      key: 'plan',
      render: (_: any, record: any) => record.plan?.name || 'N/A',
    },
    {
      title: 'Amount',
      key: 'amount',
      render: (_: any, record: any) => (
        <Text strong>
          {record.currencySymbol} {record.totalAmount}
        </Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{status?.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (date: string) => date ? moment(date).format('MMM DD, YYYY') : 'N/A',
    },
  ];

  // Orders Columns
  const ordersColumns = [
    {
      title: 'Order ID',
      dataIndex: 'orderId',
      key: 'orderId',
    },
    {
      title: 'Customer',
      key: 'customer',
      render: (_: any, record: any) => record.customerName || 'N/A',
    },
    {
      title: 'Amount',
      key: 'amount',
      render: (_: any, record: any) => (
        <Text strong>
          {record.currency_symbol} {record.amount}
        </Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'orderStatus',
      key: 'orderStatus',
      render: (status: string) => (
        <Tag color={status === 'paid' ? 'green' : 'orange'}>
          {status?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => moment(date).format('MMM DD, YYYY HH:mm'),
    },
  ];

  // Drivers Columns
  const driversColumns = [
    {
      title: 'Name',
      key: 'name',
      render: (_: any, record: any) => `${record.firstName} ${record.lastName}`,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'Status',
      dataIndex: 'isAvailable',
      key: 'isAvailable',
      render: (status: string) => (
        <Tag color={status === 'Online' ? 'green' : 'default'}>
          {status || 'Offline'}
        </Tag>
      ),
    },
  ];

  // Warehouses Columns
  const warehousesColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
    },
    {
      title: 'City',
      dataIndex: 'city',
      key: 'city',
    },
  ];

  if (loading) {
    return (
      <MainLayout>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      </MainLayout>
    );
  }

  if (!store) {
    return (
      <MainLayout>
        <Empty description="Store not found" />
      </MainLayout>
    );
  }

  const currentPlan = storePlan?.plan;
  const planData = storePlan;

  return (
    <MainLayout>
      <div style={{ marginBottom: 24 }}>
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(routes.SUPER_DASHBOARD)}
          >
            Back to Stores
          </Button>
          <Title level={2} style={{ margin: 0 }}>
            <ShopOutlined style={{ marginRight: 8 }} />
            {store.name}
          </Title>
        </Space>
      </div>

      {/* Store Information Card */}
      <Card title="Store Information" style={{ marginBottom: 24 }}>
        <Descriptions bordered column={2}>
          <Descriptions.Item label="Store Name">{store.name}</Descriptions.Item>
          <Descriptions.Item label="Shop URL">
            <a href={store.shopUrl} target="_blank" rel="noopener noreferrer">
              {store.shopUrl}
            </a>
          </Descriptions.Item>
          <Descriptions.Item label="Details">{store.details}</Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={store.status ? 'green' : 'red'}>
              {store.status ? 'Active' : 'Inactive'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Created At">
            {moment(store.createdAt).format('MMMM DD, YYYY')}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Current Plan & Usage Stats */}
      {currentPlan && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} lg={12}>
            <Card title="Current Plan" extra={<Tag color="green">Active</Tag>}>
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div>
                  <Title level={4} style={{ margin: 0 }}>
                    {currentPlan.name}
                    {currentPlan.planType === 'one_time' ? (
                      <Tag color="purple" style={{ marginLeft: 8 }}>One-Time</Tag>
                    ) : (
                      <Tag color="blue" style={{ marginLeft: 8 }}>Subscription</Tag>
                    )}
                  </Title>
                  <Text strong style={{ fontSize: 18, color: '#1890ff' }}>
                    {currentPlan.currencySymbol} {currentPlan.price}
                    {currentPlan.planType === 'subscription' && ` / ${currentPlan.billingPeriod}`}
                  </Text>
                </div>
                {planData?.endDate && (
                  <Alert
                    message={`Plan expires on ${moment(planData.endDate).format('MMMM DD, YYYY')}`}
                    type="info"
                    showIcon
                  />
                )}
                {!planData?.endDate && currentPlan.planType === 'one_time' && (
                  <Alert
                    message="Lifetime Plan - No Expiration"
                    type="success"
                    showIcon
                  />
                )}
              </Space>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Usage Statistics">
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <div>
                  <Space>
                    <HomeOutlined />
                    <Text>Warehouses</Text>
                  </Space>
                  <Progress
                    percent={getUsagePercentage(planData?.currentWarehouses || 0, currentPlan.maxWarehouses)}
                    format={() => `${planData?.currentWarehouses || 0} / ${currentPlan.maxWarehouses}`}
                    status={getUsagePercentage(planData?.currentWarehouses || 0, currentPlan.maxWarehouses) >= 90 ? 'exception' : 'active'}
                  />
                </div>
                <div>
                  <Space>
                    <TeamOutlined />
                    <Text>Drivers</Text>
                  </Space>
                  <Progress
                    percent={getUsagePercentage(planData?.currentDrivers || 0, currentPlan.maxDrivers)}
                    format={() => `${planData?.currentDrivers || 0} / ${currentPlan.maxDrivers}`}
                    status={getUsagePercentage(planData?.currentDrivers || 0, currentPlan.maxDrivers) >= 90 ? 'exception' : 'active'}
                  />
                </div>
                {currentPlan.maxOrders && (
                  <div>
                    <Space>
                      <ShoppingCartOutlined />
                      <Text>Orders (Monthly)</Text>
                    </Space>
                    <Progress
                      percent={getUsagePercentage(planData?.currentOrders || 0, currentPlan.maxOrders)}
                      format={() => `${planData?.currentOrders || 0} / ${currentPlan.maxOrders}`}
                      status={getUsagePercentage(planData?.currentOrders || 0, currentPlan.maxOrders) >= 90 ? 'exception' : 'active'}
                    />
                  </div>
                )}
              </Space>
            </Card>
          </Col>
        </Row>
      )}

      {!currentPlan && (
        <Card style={{ marginBottom: 24 }}>
          <Alert
            message="No Active Plan"
            description="This store does not have an active plan. Please assign a plan to enable full functionality."
            type="warning"
            showIcon
            action={
              <Button 
                type="primary" 
                onClick={() => navigate(routes.SUPER_DASHBOARD)}
              >
                Assign Plan
              </Button>
            }
          />
        </Card>
      )}

      {/* Activity Tabs */}
      <Card>
        <Tabs defaultActiveKey="overview">
          <TabPane 
            tab={
              <span>
                <HistoryOutlined />
                Plan History
              </span>
            } 
            key="history"
          >
            <Table
              columns={planHistoryColumns}
              dataSource={planHistory}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              locale={{ emptyText: 'No plan history found' }}
            />
          </TabPane>
          
          <TabPane 
            tab={
              <span>
                <FileTextOutlined />
                Invoices ({invoices.length})
              </span>
            } 
            key="invoices"
          >
            <Table
              columns={invoicesColumns}
              dataSource={invoices}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              locale={{ emptyText: 'No invoices found' }}
            />
          </TabPane>
          
          <TabPane 
            tab={
              <span>
                <ShoppingCartOutlined />
                Orders ({orders.length})
              </span>
            } 
            key="orders"
          >
            <Table
              columns={ordersColumns}
              dataSource={orders}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              locale={{ emptyText: 'No orders found' }}
            />
          </TabPane>
          
          <TabPane 
            tab={
              <span>
                <TeamOutlined />
                Drivers ({drivers.length})
              </span>
            } 
            key="drivers"
          >
            <Table
              columns={driversColumns}
              dataSource={drivers}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              locale={{ emptyText: 'No drivers found' }}
            />
          </TabPane>
          
          <TabPane 
            tab={
              <span>
                <HomeOutlined />
                Warehouses ({warehouses.length})
              </span>
            } 
            key="warehouses"
          >
            <Table
              columns={warehousesColumns}
              dataSource={warehouses}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              locale={{ emptyText: 'No warehouses found' }}
            />
          </TabPane>
        </Tabs>
      </Card>
    </MainLayout>
  );
};

export default StoreDetails;
